<template>
  <nav
    class="bg-[#293146] dark:bg-gray-900 border-b border-[#3b4660] dark:border-gray-700 w-full z-50"
  >
    <!-- Backdrop for mobile menu -->
    <div
      v-if="isMenuOpen"
      @click="isMenuOpen = false"
      class="fixed inset-0 bg-black/50 z-[-1] lg:hidden"
    ></div>

    <div class="max-w-screen-xl flex flex-wrap items-center justify-between mx-auto p-3 py-3">
      <router-link to="/" class="flex items-center space-x-2">
        <span
          class="self-center text-2xl font-bold whitespace-nowrap text-white dark:text-white tracking-tight"
          >lokerjambi<span class="text-[#C5B47A] dark:text-[#C5B47A]">.co.id</span></span
        >
      </router-link>
      <div class="flex md:order-2 space-x-2 md:space-x-3 items-center">
        <!-- Language Switcher Desktop -->
        <button
          @click="toggleLanguage"
          class="hidden lg:flex items-center text-gray-300 dark:text-gray-400 hover:text-white dark:hover:text-white transition-colors p-2 text-xs font-bold uppercase tracking-wider border border-gray-600 dark:border-gray-600 rounded-lg hover:border-gray-400 dark:hover:border-gray-500"
        >
          <Icon icon="mdi:earth" class="w-4 h-4 mr-1.5" />
          {{ locale }}
        </button>

        <div class="group relative hidden lg:block">
          <router-link
            to="/bookmarks"
            class="text-gray-300 dark:text-gray-400 hover:text-white dark:hover:text-white transition-colors p-2 flex items-center relative"
            :title="$t('saved.navTitle')"
          >
            <Icon icon="mdi:bookmark-outline" class="w-5 h-5" />
            <span
              v-if="bookmarkCount > 0"
              class="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full border border-[#293146] dark:border-gray-900 text-xs font-bold text-white flex items-center justify-center leading-none"
              >{{ bookmarkCount > 9 ? '9+' : bookmarkCount }}</span
            >
          </router-link>
          <div
            class="absolute bottom-full left-1/2 -translate-x-1/2 mb-0.5 px-2 py-0.5 bg-gray-800 dark:bg-gray-700 text-white dark:text-gray-100 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap"
          >
            {{ $t('saved.navTitle') }}
          </div>
        </div>

        <router-link
          to="/post-job"
          class="hidden xl:block text-[#C5B47A] dark:text-[#C5B47A] hover:text-[#293146] dark:hover:text-gray-900 border-2 border-[#C5B47A] dark:border-[#C5B47A] hover:bg-[#C5B47A] dark:hover:bg-[#C5B47A] focus:ring-4 focus:outline-none focus:ring-[#C5B47A]/50 dark:focus:ring-[#C5B47A]/40 font-semibold rounded-lg text-xs md:text-sm px-3 py-1.5 md:px-5 md:py-2.5 text-center transition-all shadow-sm"
        >
          {{ $t('home.postJob') }}
        </router-link>

        <button
          @click="isMenuOpen = !isMenuOpen"
          type="button"
          class="inline-flex items-center p-2 w-10 h-10 justify-center text-sm rounded-lg lg:hidden hover:bg-[#3b4660] dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500"
          aria-controls="navbar-cta"
          :aria-expanded="isMenuOpen"
          :class="isMenuOpen ? 'text-white dark:text-white' : 'text-gray-300 dark:text-gray-400'"
        >
          <span class="sr-only">Open main menu</span>
          <Icon v-if="!isMenuOpen" icon="mdi:menu" class="w-5 h-5" />
          <Icon v-else icon="mdi:close" class="w-5 h-5" />
        </button>
      </div>
      <div
        :class="{ hidden: !isMenuOpen, flex: isMenuOpen }"
        class="items-center justify-between w-full lg:flex lg:w-auto lg:order-1"
        id="navbar-cta"
      >
        <ul
          class="flex flex-col font-medium p-4 lg:p-0 mt-4 border border-[#3b4660] dark:border-gray-700 rounded-lg bg-[#293146] dark:bg-gray-900 lg:flex-row lg:space-x-8 lg:mt-0 lg:border-0 lg:bg-transparent w-full"
        >
          <li>
            <router-link
              to="/"
              :class="[
                'flex items-center py-2 px-3 rounded lg:p-0 transition-colors',
                isActive('/')
                  ? 'text-[#C5B47A] dark:text-[#C5B47A] lg:text-[#C5B47A] dark:lg:text-[#C5B47A] bg-[#3b4660] dark:bg-gray-700 lg:bg-transparent'
                  : 'text-gray-200 dark:text-gray-300 hover:bg-[#3b4660] dark:hover:bg-gray-700 lg:hover:text-[#C5B47A] dark:lg:hover:text-[#C5B47A]',
              ]"
              @click="isMenuOpen = false"
            >
              <Icon icon="mdi:magnify" class="w-4 h-4 mr-1.5" />
              {{ $t('home.jobs') }}
            </router-link>
          </li>

          <li>
            <a
              :href="blogUrl"
              class="flex items-center py-2 px-3 text-gray-200 dark:text-gray-300 rounded hover:bg-[#3b4660] dark:hover:bg-gray-700 lg:hover:bg-transparent lg:hover:text-[#C5B47A] dark:lg:hover:text-[#C5B47A] lg:p-0 transition-colors"
            >
              <Icon icon="mdi:lightbulb-outline" class="w-4 h-4 mr-1.5" />
              {{ $t('home.jobTips') }}
            </a>
          </li>
          <li>
            <router-link
              to="/history"
              :class="[
                'flex items-center py-2 px-3 rounded lg:p-0 transition-colors',
                isActive('/history')
                  ? 'text-[#C5B47A] dark:text-[#C5B47A] lg:text-[#C5B47A] dark:lg:text-[#C5B47A] bg-[#3b4660] dark:bg-gray-700 lg:bg-transparent'
                  : 'text-gray-200 dark:text-gray-300 hover:bg-[#3b4660] dark:hover:bg-gray-700 lg:hover:text-[#C5B47A] dark:lg:hover:text-[#C5B47A]',
              ]"
              @click="isMenuOpen = false"
            >
              <Icon icon="mdi:history" class="w-4 h-4 mr-1.5" />
              {{ $t('history.title') }}
            </router-link>
          </li>
          <li>
            <router-link
              to="/contact"
              :class="[
                'flex items-center py-2 px-3 rounded lg:p-0 transition-colors',
                isActive('/contact')
                  ? 'text-[#C5B47A] dark:text-[#C5B47A] lg:text-[#C5B47A] dark:lg:text-[#C5B47A] bg-[#3b4660] dark:bg-gray-700 lg:bg-transparent'
                  : 'text-gray-200 dark:text-gray-300 hover:bg-[#3b4660] dark:hover:bg-gray-700 lg:hover:text-[#C5B47A] dark:lg:hover:text-[#C5B47A]',
              ]"
              @click="isMenuOpen = false"
            >
              <Icon icon="mdi:email-outline" class="w-4 h-4 mr-1.5" />
              {{ $t('nav.contact') }}
            </router-link>
          </li>
          <!-- Language Switcher Mobile -->
          <li class="lg:hidden border-t border-[#3b4660] dark:border-gray-700 mt-2 pt-2">
            <button
              @click="toggleLanguage"
              class="flex items-center justify-between w-full py-2 px-3 text-gray-200 dark:text-gray-300 rounded hover:bg-[#3b4660] dark:hover:bg-gray-700 transition-colors"
            >
              <div class="flex items-center">
                <Icon icon="mdi:earth" class="w-4 h-4 mr-1.5" />
                {{ locale === 'id' ? 'Bahasa Indonesia' : 'English' }}
              </div>
              <span class="text-xs font-bold uppercase text-[#C5B47A] dark:text-[#C5B47A]">{{
                locale
              }}</span>
            </button>
          </li>
          <li class="lg:hidden">
            <router-link
              to="/bookmarks"
              class="flex items-center justify-between py-2 px-3 text-gray-200 dark:text-gray-300 rounded hover:bg-[#3b4660] dark:hover:bg-gray-700 lg:hover:bg-transparent lg:hover:text-[#C5B47A] dark:lg:hover:text-[#C5B47A]"
              @click="isMenuOpen = false"
            >
              <div class="flex items-center">
                <Icon icon="mdi:bookmark-outline" class="w-4 h-4 mr-1.5" />
                {{ $t('saved.navTitle') }}
              </div>
              <span
                v-if="bookmarkCount > 0"
                class="w-5 h-5 bg-red-500 rounded-full text-xs font-bold text-white flex items-center justify-center leading-none"
                >{{ bookmarkCount > 9 ? '9+' : bookmarkCount }}</span
              >
            </router-link>
          </li>
          <li class="lg:hidden">
            <router-link
              to="/post-job"
              class="flex items-center py-2 px-3 text-[#C5B47A] dark:text-[#C5B47A] border-2 border-[#C5B47A] dark:border-[#C5B47A] rounded hover:bg-[#C5B47A] dark:hover:bg-[#C5B47A] hover:text-[#293146] dark:hover:text-gray-900 font-semibold transition-colors"
              @click="isMenuOpen = false"
            >
              <Icon icon="mdi:plus" class="w-4 h-4 mr-1.5" />
              {{ $t('home.postJob') }}
            </router-link>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { Icon } from '@iconify/vue';
import { useI18n } from 'vue-i18n';
import { useBookmarks } from '../../composables/useBookmarks';

const isMenuOpen = ref(false);
const { bookmarkCount } = useBookmarks();
const route = useRoute();
const { locale } = useI18n();
const blogUrl = import.meta.env.VITE_BLOG_URL;

const isActive = (path) => route.path === path;

const toggleLanguage = () => {
  locale.value = locale.value === 'id' ? 'en' : 'id';
  localStorage.setItem('user-locale', locale.value);
};

onMounted(() => {
  const savedLocale = localStorage.getItem('user-locale');
  if (savedLocale && ['id', 'en'].includes(savedLocale)) {
    locale.value = savedLocale;
  }
});
</script>
